import { CarouselModal } from '@/components/common/carousel-modal';
import { ModalButton } from '@/components/common/modal-button';
import { ModalTransportationButton } from '@/components/common/modal-transportation-button';
import { MenuReview } from '@/components/feature/shop/review-menu-slides';
import { LocationMarkerIcon, MarkerIcon, PhoneIcon, RouteIcon } from '@/components/icons';
import type { MenuItem, PoiDetails, ReviewItem } from '@/types/shop';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrientation, useTickSoundInteraction } from '@/hooks';
import { useConfigStore } from '@/stores';
import ReviewSkeleton from '@/components/feature/shop/review-skeleton';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import clsx from 'clsx';
import { AppUtils } from '@/utils/app';
import styles from './index.module.scss';

type Props = {
    menuList?: MenuItem[] | null;
    reviewList?: ReviewItem[];
    poiDetails?: PoiDetails;
    isOpen?: boolean;
    isLoading?: boolean;
    onClose?: () => void;
    onLocationCheck?: () => void;
    onGuideStart?: () => void;
    showLocationCheck?: boolean;
};

export const ReviewMenuHorizontal = ({
    menuList,
    reviewList,
    poiDetails,
    isLoading,
    isOpen = false,
    onClose = () => {},
    onGuideStart,
    onLocationCheck,
    showLocationCheck = true,
}: Props) => {
    const { t } = useTranslation();
    const { isHorizontal } = useOrientation();
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const currentLanguage = useConfigStore((state) => state.config.ui.language);
    const { playTick } = useTickSoundInteraction();

    const isShowReviews = useMemo(() => {
        if (!Array.isArray(reviewList) || !reviewList?.length || currentLanguage !== 'ko') {
            return false;
        }
        return true;
    }, [reviewList, currentLanguage]);

    const poiInfoClassname = useMemo(() => {
        return clsx({
            [`flex h-[calc(131*var(--vh-unit))] items-center justify-between px-[calc(56*var(--vw-unit))]`]: currentLanguage === 'ko',
            [`flex flex-col widescreen:h-[calc(132*var(--vh-unit))] h-[calc(156*var(--vh-unit))] gap-[calc(12*var(--vh-unit))] items-start justify-center px-[calc(56*var(--vw-unit))]`]:
                currentLanguage !== 'ko',
        });
    }, [currentLanguage]);

    if (!isOpen) {
        return null;
    }

    return (
        <CarouselModal
            isLoading={isLoading}
            isOpen={isOpen}
            onClose={onClose}
            slides={
                isLoading && currentLanguage === 'ko' ? (
                    <ReviewSkeleton />
                ) : (
                    <MenuReview showReview={isShowReviews} reviewList={reviewList} menuList={menuList} />
                )
            }
            closeIconTabIndex={8}
            closeIconRegionOrder={8}
        >
            <div className="widescreen:pt-[calc(24*var(--vh-unit))] h-fit pt-[calc(36*var(--vh-unit))] pb-[calc(52*var(--vh-unit))]">
                <div className="flex h-[calc(132*var(--vh-unit))] items-center justify-center px-[calc(56*var(--vw-unit))]">
                    <h1
                        data-modal-region
                        data-modal-region-order={isHorizontal ? 3 : 1}
                        tabIndex={isHorizontal ? 3 : 1}
                        data-interactive
                        aria-label={poiDetails?.name}
                        className="text-text-main01 focus-outline-style line-clamp-2 text-[calc(40*var(--vh-unit))] font-bold"
                    >
                        {poiDetails?.name}
                    </h1>
                </div>
                <div className="flex items-center justify-center">
                    <div
                        tabIndex={isHorizontal ? 4 : 2}
                        data-interactive
                        aria-label={t('shop.modal.tts.carousel.shop_name', { poiName: poiDetails?.name })}
                        className="widescreen:h-[calc(500*var(--vh-unit))] focus-outline-style h-[calc(560*var(--vh-unit))] w-[calc(568*var(--vw-unit))] rounded-md"
                    >
                        {poiDetails?.imageSrc ? (
                            <img
                                src={poiDetails?.imageSrc}
                                alt={poiDetails?.name}
                                className={clsx(['widescreen:aspect-[568/500] aspect-[568/560]'], [styles['img_contain']])}
                            />
                        ) : (
                            <PlaceHolder />
                        )}
                    </div>
                </div>

                <div
                    className={clsx(
                        poiInfoClassname,
                        currentLanguage === 'ko'
                            ? 'grid grid-cols-[1fr_auto] items-center gap-[calc(16*var(--vw-unit))]'
                            : 'flex flex-col gap-[calc(12*var(--vh-unit))]',
                    )}
                >
                    {/* ADDRESS / FLOOR / CATEGORY */}
                    <div
                        tabIndex={isHorizontal ? 5 : 3}
                        data-interactive
                        aria-label={t('shop.modal.tts.carousel.address', {
                            buildingName: buildings && buildings.length > 1 ? poiDetails?.address : '',
                            floor: AppUtils.getFloorTTSText(poiDetails?.floorName || ''),
                            category: poiDetails?.category,
                        })}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                playTick()
                                e.preventDefault()
                                e.stopPropagation()
                            }
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        className="focus-border flex min-w-0 items-center gap-[calc(8*var(--vw-unit))]"
                    >
                        <div className="flex-shrink-0">
                            <LocationMarkerIcon width={36} height={36} />
                        </div>

                        <div className="flex min-w-0 items-center">
                            {buildings?.length && buildings.length > 1 && (
                                <>
                                    <span className="mr-[calc(12*var(--vw-unit))] max-w-[calc(130*var(--vw-unit))] truncate text-[calc(24*var(--vh-unit))] leading-[calc(36*var(--vh-unit))] text-text-sub01">
                                        {poiDetails?.address}
                                    </span>
                                    <div className="mr-[calc(12*var(--vw-unit))] h-[calc(20*var(--vh-unit))] w-[calc(2*var(--vw-unit))] flex-shrink-0 bg-text-sub01" />
                                </>
                            )}

                            <span className="mr-[calc(12*var(--vw-unit))] flex-shrink-0 text-[calc(26*var(--vh-unit))] font-bold text-text-main01 number">
                                {poiDetails?.floorName}
                            </span>

                            <span className="min-w-0 truncate text-[calc(24*var(--vh-unit))] leading-[calc(36*var(--vh-unit))] font-medium text-text-main01">
                                {poiDetails?.category}
                            </span>
                        </div>
                    </div>

                    {/* PHONE */}
                    {poiDetails?.phoneNumber && (
                        <div
                            tabIndex={isHorizontal ? 5 : 3}
                            data-interactive
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    playTick()
                                    e.preventDefault()
                                    e.stopPropagation()
                                }
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                            aria-label={`${t('common.tts.phone_number')} ${AppUtils.getPhoneNumberTTSText(
                                poiDetails?.phoneNumber,
                            )}`}
                            className={clsx(
                                'focus-border flex items-center gap-[calc(8*var(--vw-unit))] whitespace-nowrap',
                            )}
                        >
                            <div className="flex-shrink-0">
                                <PhoneIcon width={36} height={36} />
                            </div>
                            <span className="text-[calc(26*var(--vh-unit))] font-medium text-text-main01 number">
                                {poiDetails?.phoneNumber}
                            </span>
                        </div>
                    )}
                </div>

                {isHorizontal ? (
                    <div className="flex items-center justify-center gap-[calc(23*var(--vw-unit))]">
                        {showLocationCheck && (
                            <ModalTransportationButton
                                variant="primary"
                                icon={<MarkerIcon width={60} height={60} />}
                                tabIndex={6}
                                role="button"
                                data-interactive
                                aria-label={t('common.button.location_confirmation')}
                                onClick={() => onLocationCheck?.()}
                                data-role={t('common.data_role.button')}
                            >
                                {t('common.button.location_confirmation')}
                            </ModalTransportationButton>
                        )}
                        <ModalTransportationButton
                            variant="secondary"
                            icon={<RouteIcon width={60} height={60} />}
                            onClick={() => onGuideStart?.()}
                            tabIndex={7}
                            role="button"
                            data-interactive
                            aria-label={t('common.button.direction')}
                            data-role={t('common.data_role.button')}
                        >
                            {t('common.button.direction')}
                        </ModalTransportationButton>
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-[calc(23*var(--vw-unit))]">
                        {showLocationCheck && (
                            <ModalButton
                                size="lg"
                                variant="primary"
                                icon={<MarkerIcon width={80} height={80} />}
                                tabIndex={4}
                                role="button"
                                data-interactive
                                aria-label={t('common.button.location_confirmation')}
                                onClick={() => onLocationCheck?.()}
                                data-role={t('common.data_role.button')}
                            >
                                {t('common.button.location_confirmation')}
                            </ModalButton>
                        )}
                        <ModalButton
                            size="lg"
                            variant="secondary"
                            icon={<RouteIcon width={80} height={80} />}
                            onClick={() => onGuideStart?.()}
                            tabIndex={5}
                            role="button"
                            data-interactive
                            aria-label={t('common.button.direction')}
                            data-role={t('common.data_role.button')}
                        >
                            {t('common.button.direction')}
                        </ModalButton>
                    </div>
                )}
            </div>
        </CarouselModal>
    );
};
